import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { Setting } from '@/types';
import { Save } from 'lucide-react';
import { useState } from 'react';

interface NumberEditorProps {
    setting: Setting;
    onUpdate: (key: string, value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
}

export default function NumberEditor({ setting, onUpdate, min, max, step = 1, placeholder }: NumberEditorProps) {
    const { t } = useTranslations();
    const currentValue = typeof setting.value === 'number' ? setting.value : typeof setting.value === 'string' ? Number(setting.value) || 0 : 0;

    const [value, setValue] = useState<string>(currentValue.toString());
    const [error, setError] = useState<string | null>(null);

    const validate = (candidate: string): string | null => {
        const numeric = Number(candidate);

        if (Number.isNaN(numeric) || candidate.trim() === '') {
            return t('settings.editors.number.invalid', 'Please enter a valid number');
        }

        if (min !== undefined && numeric < min) {
            return t('settings.editors.number.min', 'Value must be at least :min').replace(':min', String(min));
        }

        if (max !== undefined && numeric > max) {
            return t('settings.editors.number.max', 'Value must be at most :max').replace(':max', String(max));
        }

        return null;
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
        // An empty field is "not filled in yet" rather than invalid; only flag it on save.
        setError(newValue === '' ? null : validate(newValue));
    };

    const handleSave = () => {
        const validationError = validate(value);

        if (validationError) {
            setError(validationError);

            return;
        }

        setError(null);
        onUpdate(setting.key, Number(value));
    };

    const rangeHint = () => {
        if (min !== undefined && max !== undefined) {
            return t('settings.editors.number.hint_between', 'Enter a value between :min and :max')
                .replace(':min', String(min))
                .replace(':max', String(max));
        }

        if (min !== undefined) {
            return t('settings.editors.number.hint_min', 'Enter a value of at least :min').replace(':min', String(min));
        }

        if (max !== undefined) {
            return t('settings.editors.number.hint_max', 'Enter a value of at most :max').replace(':max', String(max));
        }

        return t('settings.editors.number.hint_any', 'Enter a numeric value');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{t('settings.editors.number.current_value', 'Current value')}</Label>
                <span className="text-sm font-medium">{currentValue}</span>
            </div>

            <div className="space-y-2">
                <Label htmlFor={`${setting.key}-input`}>{t('settings.editors.number.new_value', 'New value')}</Label>
                <div className="flex gap-2">
                    <Input
                        id={`${setting.key}-input`}
                        type="number"
                        step={step}
                        min={min}
                        max={max}
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={placeholder ?? t('settings.editors.number.placeholder', 'Enter a number...')}
                        className={error ? 'border-destructive' : ''}
                    />
                    <Button onClick={handleSave} disabled={!!error || value === currentValue.toString()}>
                        <Save className="mr-2 h-4 w-4" />
                        {t('settings.editors.number.save', 'Save')}
                    </Button>
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
            </div>

            <p className="text-muted-foreground text-xs">{rangeHint()}</p>
        </div>
    );
}
